import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { listAntiAbuseFlags, resolveAntiAbuseFlag } from '../lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBox,
  Label,
  Loading,
  PageHeader,
  TextInput,
  formatDate,
} from '../components/ui';

export function AntiAbusePage(): JSX.Element {
  const [resolved, setResolved] = useState(false);
  const q = useQuery({
    queryKey: ['aa-flags', resolved],
    queryFn: () => listAntiAbuseFlags(resolved),
  });

  return (
    <>
      <PageHeader
        title="Anti-abuse flags"
        subtitle="Rides flagged automatically by AA-01 (frequency) or AA-03 (price + unproven driver)."
      />
      <div className="mb-4 flex gap-2">
        <Button
          variant={!resolved ? 'primary' : 'secondary'}
          onClick={() => setResolved(false)}
        >
          Open
        </Button>
        <Button
          variant={resolved ? 'primary' : 'secondary'}
          onClick={() => setResolved(true)}
        >
          Resolved
        </Button>
      </div>
      {q.isLoading && <Loading />}
      {q.isError && <ErrorBox message={(q.error as Error).message} />}
      {q.data && q.data.items.length === 0 && <EmptyState>No flags here.</EmptyState>}
      <div className="space-y-3">
        {q.data?.items.map((f) => <FlagCard key={f.id} flag={f} />)}
      </div>
    </>
  );
}

function FlagCard({ flag }: { flag: { id: string; userId: string; userDisplayName: string | null; ruleId: string; severity: number; rideId: string | null; details: Record<string, unknown>; resolved: boolean; resolutionNote: string | null; createdAt: number } }): JSX.Element {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const mut = useMutation({
    mutationFn: () => resolveAntiAbuseFlag(flag.id, note),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['aa-flags'] }),
  });

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-brand-navy">
            <Badge tone={flag.severity >= 3 ? 'danger' : 'warning'}>{flag.ruleId}</Badge>{' '}
            <span className="ml-2">{flag.userDisplayName ?? flag.userId.slice(0, 12)}</span>
          </p>
          <p className="text-xs text-text-soft">
            sev {flag.severity} · {formatDate(flag.createdAt)}
            {flag.rideId && ` · ride ${flag.rideId.slice(0, 14)}`}
          </p>
        </div>
        {flag.resolved && <Badge tone="success">Resolved</Badge>}
      </div>
      <pre className="mt-2 overflow-x-auto rounded bg-bg px-3 py-2 text-xs text-text-soft">
        {JSON.stringify(flag.details, null, 2)}
      </pre>
      {flag.resolved ? (
        flag.resolutionNote && (
          <p className="mt-2 text-xs text-text-soft">
            <strong>Note:</strong> {flag.resolutionNote}
          </p>
        )
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
          <div>
            <Label htmlFor={`note-${flag.id}`}>Resolution note</Label>
            <TextInput
              id={`note-${flag.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Required for audit log"
            />
          </div>
          <div className="self-end">
            <Button onClick={() => mut.mutate()} disabled={!note.trim() || mut.isPending}>
              {mut.isPending ? 'Resolving…' : 'Resolve'}
            </Button>
          </div>
        </div>
      )}
      {mut.isError && (
        <div className="mt-2">
          <ErrorBox message={(mut.error as Error).message} />
        </div>
      )}
    </Card>
  );
}
